import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { Button } from '@mobi/component-library/Base/Buttons'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { VerificationCodeInput } from '@mobi/component-library/Selection&Input/Inputs/VerificationCodeInput'
import type { MfaAnalyticsData } from '@core/Services/Analytics/helpers/dataLayer/types'
import { isCodeMismatchError, isRateLimitError, trackMfaEvent } from '../Helpers/helpers'
import { calculateTimeLeft, formatTime } from '../Helpers/utils'
import { MfaCodePageHeader } from '../Pages/VerificationCodePage.styles'

interface VerificationCodeFormProps {
  /**
   * Must be an async operation that either succeeds or throws a specific error type.
   * Loading state is managed internally by this component.
   * @param code - The 6-digit verification code to verify.
   * @throws {VerificationStandardError} When verification fails. Constructor accepts error message.
   * Error message is used to set error state internally.
   */
  onVerify: (code: string) => Promise<void>
  /**
   * Requests a new verification code to be sent.
   * Must be an async operation that either succeeds or throws a specific error type.
   * Loading state is managed internally by this component.
   * @throws {Error} When resend operation fails. Constructor accepts error message.
   * Error message is used to set error state internally.
   */
  onResend: () => Promise<void>
  shouldShowHeader?: boolean
  title?: string
  children?: React.ReactNode
  // these should only be used for GA tracking
  analyticsFlowName?: MfaAnalyticsData['otpFlow']
  analyticsMethodName?: MfaAnalyticsData['otpMethod']
}

export const VerificationCodeForm: React.FC<VerificationCodeFormProps> = ({
  onVerify,
  onResend,
  shouldShowHeader = true,
  title,
  children,
  analyticsFlowName,
  analyticsMethodName,
}) => {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [isVerifyLoading, setIsVerifyLoading] = useState(false)
  const [isResendLoading, setIsResendLoading] = useState(false)
  const [expiryTime, setExpiryTime] = useState(Date.now() + 3 * 60 * 1000)
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(expiryTime))

  const isDisabled = isRateLimitError(error)
  const isExpired = timeLeft <= 0
  const isError = !!error

  const defaultAnalyticsProperties = {
    otpFlow: analyticsFlowName ?? 'Unknown',
    otpMethod: analyticsMethodName ?? 'Unknown',
  }

  useEffect(() => {
    const timer = setInterval(() => {
      const newTimeLeft = calculateTimeLeft(expiryTime)

      if (newTimeLeft <= 0) {
        clearInterval(timer)
      }
      setTimeLeft(newTimeLeft)
    }, 1000)

    return () => clearInterval(timer)
  }, [expiryTime])

  useEffect(() => {
    if (isDisabled == true) {
      resetVerificationState(Date.now())
    } else {
      if (!error) {
        resetVerificationState()
      }
    }
  }, [isDisabled])

  const resetVerificationState = (newExpiryTime?: number) => {
    setCode('')
    setExpiryTime(newExpiryTime || Date.now() + 3 * 60 * 1000)
  }

  const handleCodeChange = (newCode: string) => {
    if (!isDisabled) {
      setError('')
      setCode(newCode)
    }
  }

  const handleVerifyClick = async (newCode?: string) => {
    setIsVerifyLoading(true)
    const submitCode = newCode ? newCode : code

    const filledDigits = submitCode.length
    if (filledDigits < 6) {
      setError('Please enter a valid verification code')
      setIsVerifyLoading(false)
      return
    }

    try {
      await onVerify(submitCode)
      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP Submit',
      })
    } catch (error) {
      const genericError = 'Sorry, there was an error verifying the code'
      if (error instanceof Error) {
        const errorMessage = error.message ?? genericError
        if (isRateLimitError(errorMessage) || isCodeMismatchError(errorMessage)) {
          setError(errorMessage)
        } else {
          setError(genericError)
        }
      } else {
        setError(genericError)
      }

      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP Error',
        message: 'Incorrect Code',
      })
    } finally {
      setIsVerifyLoading(false)
    }
  }

  const handleResendClick = async () => {
    setIsResendLoading(true)
    setError('')
    try {
      await onResend()

      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP resend',
      })
    } catch (error) {
      const genericError = 'Sorry, there was an error resending the code'
      if (error instanceof Error) {
        const errorMessage = error.message ?? genericError
        if (isRateLimitError(errorMessage) || isCodeMismatchError(errorMessage)) {
          setError(errorMessage)
        } else {
          setError(genericError)
        }
      } else {
        setError(genericError)
      }

      trackMfaEvent({
        ...defaultAnalyticsProperties,
        interaction_type: 'OTP Error',
        message: 'Resend Failed',
      })
    } finally {
      setIsResendLoading(false)
      if (!isDisabled) {
        resetVerificationState()
      }
    }
  }

  const isVerifyDisabled =
    [...code].filter(x => !isNaN(parseInt(x))).length < 6 ||
    isError ||
    isDisabled ||
    isVerifyLoading ||
    isResendLoading
  const isInputDisabled = isDisabled || isExpired || isVerifyLoading || isResendLoading

  return (
    <MfaFormContainer data-tid-mfa-code-verification-form>
      <Flex flexDirection='column' gap={spacing.smx1}>
        {shouldShowHeader || title ? (
          <MfaCodePageHeader>{title ?? 'Verify your Identity'}</MfaCodePageHeader>
        ) : null}
        {children}
      </Flex>
      <Flex flexDirection='column' gap={spacing.sm}>
        <VerificationCodeInput
          code={code}
          onChange={handleCodeChange}
          onSubmit={handleVerifyClick}
          isValid={!isError && !isExpired}
          isDisabled={isInputDisabled}
        />

        {isError ? (
          <MfaFormMessageContainer>
            <Icon name='LineAlertCircle' size='1.6rem' color={colors.error[600]} />
            <MfaFormErrorMessage>{error}</MfaFormErrorMessage>
          </MfaFormMessageContainer>
        ) : null}

        {!isExpired && !isError && (
          <MfaCodeFormText>
            Your code will expire in <b>{formatTime(timeLeft)}</b> mins.
          </MfaCodeFormText>
        )}

        {isExpired && !isDisabled && (
          <MfaCodeFormText
            style={{
              color: colors.error[600],
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
          >
            <Icon name='LineAlertCircle' size='1.6rem' color={colors.error[600]} /> Your code has
            expired. Please request a new one.
          </MfaCodeFormText>
        )}
      </Flex>
      <Flex flexDirection='column' gap={spacing.smx1}>
        {isExpired || !!isError ? (
          <Button onClick={handleResendClick} color='primary'>
            Request new code
          </Button>
        ) : (
          <>
            <Button
              onClick={() => handleVerifyClick()}
              color='primary'
              isLoading={isVerifyLoading}
              disabled={isVerifyDisabled}
            >
              Verify
            </Button>
            <Button
              onClick={handleResendClick}
              isLoading={isResendLoading}
              disabled={isVerifyLoading}
              color='secondary_color'
            >
              Resend verification code
            </Button>
          </>
        )}
      </Flex>
    </MfaFormContainer>
  )
}

// ======
// Styles
// ======

const MfaFormContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.bigx1,
})

const MfaCodeFormText = styled.p({
  textAlign: 'left',
  margin: spacing.none,
  color: colors.neutral[900],
})

const MfaFormMessageContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  minHeight: '21px',
})

const MfaFormErrorMessage = styled.p({
  color: colors.error[600],
  ...font.size.md,
  margin: spacing.none,
})
